interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

export function Donut({
  segments,
  size = 132,
  thickness = 14,
  centerLabel,
  centerSub,
}: {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerLabel?: string | number;
  centerSub?: string;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          className="text-muted"
          strokeWidth={thickness}
        />
        {segments.map((s, i) => {
          const len = (s.value / total) * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
              style={{
                transition:
                  "stroke-dasharray .6s ease, stroke-dashoffset .6s ease",
              }}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      {centerLabel !== undefined && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold leading-none text-foreground">
            {centerLabel}
          </span>
          {centerSub && (
            <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {centerSub}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function BarTrend({
  data,
  height = 64,
}: {
  data: { week: string; deploys: number }[];
  height?: number;
}) {
  const max = Math.max(...data.map((d) => d.deploys), 1);
  return (
    <div className="flex items-end gap-1.5" style={{ height }}>
      {data.map((d, i) => (
        <div
          key={i}
          className="group flex flex-1 flex-col items-center justify-end gap-1"
          style={{ height }}
        >
          <div
            className="w-full rounded-t-[3px] bg-primary transition-all duration-500 hover:opacity-80"
            style={{
              height: `${(d.deploys / max) * (height - 16)}px`,
              minHeight: 3,
            }}
            title={`${d.deploys} deploys`}
          />
          <span className="text-[9px] font-medium text-muted-foreground">
            {d.week.replace(/^[A-Za-z]+ /, "")}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Sparkline({
  values,
  width = 64,
  height = 26,
}: {
  values: number[];
  width?: number;
  height?: number;
}) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const pts = values
    .map(
      (v, i) =>
        `${(i / (values.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`,
    )
    .join(" ");
  const lastPt = pts.split(" ").pop()!.split(",");
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={pts}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={lastPt[0]}
        cy={lastPt[1]}
        r="2.5"
        fill="hsl(var(--primary))"
      />
    </svg>
  );
}
